import React, { useState, useEffect } from 'react'
import { Button, IconButton } from '@ui'
import { StatusBadge } from '../components/StatusBadge.jsx'
import { appsyncQuery } from '@api/appsync-client'

const GET_ADMIN_USER = `
  query GetAdminUser($userId: ID!) {
    getAdminUser(userId: $userId) {
      userId email name managerId managerName team status joinedAt
      totalSessions completedSessions draftSessions lastActiveAt avgCardsPerSession
      reports
      sessionHistory { sessionId createdAt status completedAt cardCount }
    }
  }
`

export function UserDetailPanel({ userId, onClose, onDeactivate, onReactivate, onResendInvite }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) return
    setLoading(true)
    appsyncQuery(GET_ADMIN_USER, { userId })
      .then((d) => setUser(d.getAdminUser))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [userId])

  if (!userId) return null

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>{loading ? 'Loading…' : (user?.name || user?.email)}</h3>
          <IconButton ariaLabel="Close" onClick={onClose}>×</IconButton>
        </div>

        {user && (
          <>
            <div style={styles.section}>
              <Row label="Email" value={user.email} />
              <Row label="Team" value={user.team || '—'} />
              <Row label="Manager" value={user.managerName || '—'} />
              <Row label="Joined" value={user.joinedAt ? new Date(user.joinedAt).toLocaleDateString() : '—'} />
              <Row label="Status" value={<StatusBadge status={user.status} />} />
            </div>

            <div style={styles.section}>
              <div style={styles.sectionTitle}>Stats</div>
              <Row label="Total sessions" value={user.totalSessions} />
              <Row label="Completed" value={user.completedSessions} />
              <Row label="Drafts" value={user.draftSessions} />
              <Row label="Avg cards" value={user.avgCardsPerSession} />
            </div>

            {user.sessionHistory?.length > 0 && (
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Session history</div>
                {user.sessionHistory.map((s) => (
                  <div key={s.sessionId} style={styles.sessionRow}>
                    <span>{s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '—'}</span>
                    <StatusBadge status={s.status} />
                    <span style={styles.muted}>{s.cardCount} cards</span>
                  </div>
                ))}
              </div>
            )}

            <div style={styles.actions}>
              {user.status === 'invited' && (
                <Button
                  variant="secondary"
                  onClick={() => { onResendInvite(userId); onClose() }}
                  style={{ width: '100%', marginBottom: 8 }}
                >
                  Resend invite
                </Button>
              )}
              {user.status === 'deactivated' ? (
                <Button
                  variant="secondary"
                  onClick={() => { onReactivate(userId); onClose() }}
                  style={{ width: '100%' }}
                >
                  Reactivate
                </Button>
              ) : (
                <Button
                  variant="danger"
                  onClick={() => { onDeactivate(userId); onClose() }}
                  style={{ width: '100%' }}
                >
                  Deactivate
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 12, borderBottom: '0.5px solid var(--border)' }}>
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span>{value}</span>
    </div>
  )
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 50 },
  panel: {
    position: 'fixed', right: 0, top: 0, bottom: 0, width: 340,
    background: 'var(--surface)', boxShadow: '-4px 0 16px rgba(0,0,0,0.1)',
    display: 'flex', flexDirection: 'column', overflowY: 'auto',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 12px' },
  title: { fontSize: 15, fontWeight: 600 },
  section: { padding: '12px 20px', borderBottom: '1px solid var(--border)' },
  sectionTitle: { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 },
  sessionRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 12 },
  muted: { color: 'var(--text-secondary)' },
  actions: { padding: 20, marginTop: 'auto' },
}
